import React, { useCallback, useRef, useEffect, useState } from "react";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import htmlParser from "html-react-parser";
import "./BannerVideoSection.css";

const BannerVideoSection = ({ data }) => {
  const [videoLibrary, setVideoLibrary] = useState({});
  const currentPlayingVideo = useRef(null);
  
  const isMobile = window.innerWidth <= 768;
  const cdnUrl = process.env.REACT_APP_CDN;
  const jsonCdnUrl = cdnUrl + "angelai-life/jsons/videosection.json";

  // Fetch video library from CDN
  useEffect(() => {
    const fetchVideoLibrary = async () => {
      try {
        const timeoutPromise = new Promise((_, reject) => {
          setTimeout(() => reject(new Error('Fetch timeout')), 5000);
        });

        const response = await Promise.race([
          fetch(jsonCdnUrl),
          timeoutPromise
        ]);

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const videoData = await response.json();

        if (videoData && videoData.videos && Array.isArray(videoData.videos)) {
          // Convert array to object with id as key for easy lookup
          const libraryObj = {};
          videoData.videos.forEach(video => {
            libraryObj[video.id] = {
              id: video.id,
              containerId: video.containerId,
              youtubeEmbedId: video.youtubeEmbedId,
              thumbnail: video.thumbnail,
              altText: video.altText,
              description: video.description
            };
          });
          setVideoLibrary(libraryObj);
          
        } else {
          throw new Error('Invalid data structure from CDN');
        }
      } catch (error) {
        console.error('Failed to fetch video library from CDN:', error);
        setVideoLibrary({});
      }
    };

    fetchVideoLibrary();
  }, [jsonCdnUrl]);

  // Get videos configuration from data prop (master page JSON)
  const videoConfig = data?.videosSection || {
    heading: "Home Stories That Inspire",
    subtitle: "These standout journey remind us why we love what we do.",
    videoIds: []
  };

  // Get videos based on configured IDs from data.videosSection
  const displayVideos = videoConfig.videoIds
    .map(id => {
      const video = videoLibrary[id];
      return video;
    })
    .filter(Boolean)
    .map(video => ({
      ...video,
      thumbnail: video.thumbnail.startsWith('http') 
        ? video.thumbnail 
        : `${cdnUrl}angelai/angelai-new-design/${video.thumbnail}`
    }));

  const resetVideoThumbnail = useCallback((containerId) => {
    const container = document.getElementById(containerId);
    if (!container) return;

    const video = displayVideos.find(v => v.containerId === containerId);
    if (!video) return;

    const thumbnailSrc = video.thumbnail.startsWith('http') 
      ? video.thumbnail 
      : `${cdnUrl}angelai/angelai-new-design/${video.thumbnail}`;

    container.innerHTML = `
      <img
        src="${thumbnailSrc}"
        alt="${video.altText}"
        class="thumbnail-image"
        loading="lazy"
      />
      <div class="play-button">
        <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path d="M8 5v14l11-7z" fill="white"/>
        </svg>
      </div>
    `;
  }, [displayVideos, cdnUrl]);

  const handleVideoClick = useCallback((videoId, youtubeEmbedId) => {
    if (currentPlayingVideo.current && currentPlayingVideo.current !== videoId) {
      const currentContainer = document.getElementById(currentPlayingVideo.current);
      if (currentContainer) {
        resetVideoThumbnail(currentPlayingVideo.current);
      }
    }

    currentPlayingVideo.current = videoId;
    const container = document.getElementById(videoId);
    
    if (container) {
      const containerWidth = container.offsetWidth;
      const containerHeight = container.offsetHeight;
      container.style.width = containerWidth + "px";
      container.style.height = containerHeight + "px";

      container.innerHTML = `
        <div class="video-loading">
          <div style="color: white; font-size: 14px;">Loading video...</div>
        </div>
      `;

      setTimeout(() => {
        container.innerHTML = `
          <div class="video-container-mask">
            <div class="video-player-wrapper">
              <div class="close-video-btn" data-video-id="${videoId}">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M18 6L6 18" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M6 6L18 18" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </div>
              <iframe
                src="https://www.youtube.com/embed/${youtubeEmbedId}?rel=0&modestbranding=1&autoplay=1&controls=1&mute=0&playsinline=1"
                title="YouTube video"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                style="width: 100%; height: 100%; border: 0; border-radius: 12px;">
              </iframe>
            </div>
          </div>
        `;
        container.style.width = "";
        container.style.height = "";
        
        // Add click event listener to the close button
        const closeBtn = container.querySelector('.close-video-btn');
        if (closeBtn) {
          closeBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            resetVideoThumbnail(videoId);
            if (currentPlayingVideo.current === videoId) {
              currentPlayingVideo.current = null;
            }
          });
        }
      }, 50);
    }
  }, [resetVideoThumbnail]);
 
  return (
    <div className="home-stories-section">
      <Container>
        <div className="section-header">
          <h2 className="section-heading">{htmlParser(videoConfig.heading)}</h2>
          <p className="section-subtitle">
            {htmlParser(videoConfig.subtitle)}
          </p>
        </div>

        <Row>
          {displayVideos.map((video) => (
            <>
              <Col key={video.id} lg={5} md={5} sm={12} className="video-col">
                <div className="video-card">
                  <div
                    className="video-thumbnail"
                    onClick={() => handleVideoClick(video.containerId, video.youtubeEmbedId)}
                    id={video.containerId}
                  >
                    <img
                      src={video.thumbnail}
                      alt={video.altText}
                      className="thumbnail-image"
                      loading="lazy"
                    />
                    <div className="play-button">
                      <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path d="M8 5v14l11-7z" fill="white" />
                      </svg>
                    </div>
                  </div>
                  <div className="video-description">
                    <p>{htmlParser(video.description)}</p>
                  </div>
                </div>
              </Col>
              <Col></Col>
            </>
          ))}
        </Row>
      </Container>
    </div>
  );
};

export default BannerVideoSection;